import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useRequests, REQ_TYPES, CHANNELS, CONTACT_ROLES, type Request, type ReqType, type ReqStatus, type Channel, type Dept } from '../RequestsContext';

const STATUSES: ReqStatus[] = ['new', 'classified', 'in_progress', 'waiting_client', 'blocked', 'escalated', 'closed'];
const DEPTS: Dept[] = ['csr', 'sales', 'prepress', 'logistics', 'production', 'quality', 'accounting', 'operations'];
const MISSING = ['PO', 'Artwork', 'Quantity', 'Due date', 'SKU', 'Delivery info'];
const chLabel: Record<string, string> = { phone: 'Call', email: 'Email', text: 'Text', website: 'Web', portal: 'Portal', verbal: 'Verbal', system: 'System' };

export default function RequestWorkspace({ request, onClose }: { request: Request; onClose: () => void }) {
  const R = useRequests();
  const r = R.requests.find((x) => x.id === request.id) || request;
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDept, setTaskDept] = useState<Dept>('csr');
  const [taskOwner, setTaskOwner] = useState('Fatima');
  const [msgCh, setMsgCh] = useState<Channel>('email');
  const [msgDir, setMsgDir] = useState<'in' | 'out'>('out');
  const [msg, setMsg] = useState('');
  const [missSel, setMissSel] = useState<string[]>([]);
  const [fuDate, setFuDate] = useState('');
  const [fuNote, setFuNote] = useState('');
  const [esc, setEsc] = useState('');
  const [closeNote, setCloseNote] = useState('');
  const [cName, setCName] = useState(r.contact?.name || '');
  const [cRole, setCRole] = useState(r.contact?.role || 'Buyer');
  const [cEmail, setCEmail] = useState(r.contact?.email || '');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  const Section = ({ n, title, children }: { n: number; title: string; children: ReactNode }) => (
    <div className="req-sec"><div className="req-sec-h"><span className="req-sec-n">{n}</span>{title}</div>{children}</div>
  );

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <aside className="drawer req-drawer">
        <div className="drawer-head">
          <button className="drawer-close" onClick={onClose}>×</button>
          <div className="drawer-title">{r.id}</div>
          <div className="drawer-tags">
            <span className="tag dp-csr">{r.clientName}</span>
            <span className={`tag st-${r.status === 'closed' ? 'completed' : r.status === 'escalated' || r.status === 'blocked' ? 'blocked' : 'in_progress'}`}>{r.status.replace('_', ' ')}</span>
            <span className="tag pr-standard">{r.type}</span>
            <span className="chan-badge ch-email">{chLabel[r.source]}</span>
          </div>
          <p className="client-summary" style={{ marginTop: 10 }}>{r.summary}</p>
        </div>

        <div className="drawer-body req-body">
          <Section n={2} title="Match Client">
            <div className="req-inline"><span className="fact-k">Linked client</span><strong>{r.clientName}</strong></div>
          </Section>

          <Section n={3} title="Client Contact">
            <div className="form-grid">
              <div className="field"><label>Name</label><input value={cName} onChange={(e) => setCName(e.target.value)} /></div>
              <div className="field"><label>Role</label><select value={cRole} onChange={(e) => setCRole(e.target.value)}>{CONTACT_ROLES.map((x) => <option key={x}>{x}</option>)}</select></div>
              <div className="field full"><label>Email</label><input value={cEmail} onChange={(e) => setCEmail(e.target.value)} /></div>
            </div>
            <button className="btn small" style={{ marginTop: 8 }} onClick={() => cName && R.setContact(r.id, { name: cName, role: cRole, email: cEmail })}>Save contact</button>
          </Section>

          <Section n={4} title="Classify Request">
            <select value={r.type} onChange={(e) => R.classify(r.id, e.target.value as ReqType)}>
              <option value="unclassified">Unclassified</option>
              {REQ_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </Section>

          <Section n={5} title="Create &amp; Assign Task">
            <div className="form-grid">
              <div className="field full"><label>Task</label><input placeholder="e.g. Prepare sample request" value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} /></div>
              <div className="field"><label>Department</label><select value={taskDept} onChange={(e) => setTaskDept(e.target.value as Dept)}>{DEPTS.map((d) => <option key={d}>{d}</option>)}</select></div>
              <div className="field"><label>Owner</label><input value={taskOwner} onChange={(e) => setTaskOwner(e.target.value)} /></div>
            </div>
            <button className="btn small primary" style={{ marginTop: 8 }} onClick={() => { if (taskTitle) { R.createTask(r.id, taskTitle, taskDept, taskOwner); setTaskTitle(''); } }}>Create &amp; assign</button>
            <div className="mini-list" style={{ marginTop: 10 }}>
              {r.tasks.map((t) => (
                <div className="mini-row" key={t.id}>
                  <div className="mr-main"><strong>{t.title}</strong><div className="mr-sub">{t.id} · {t.owner} · {t.dept}</div></div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <span className={`tag st-${t.status === 'done' ? 'completed' : t.status === 'assigned' ? 'assigned' : t.status === 'acknowledged' ? 'acknowledged' : 'in_progress'}`}>{t.status}</span>
                    {t.status === 'assigned' && <button className="btn small success" onClick={() => R.acknowledgeTask(r.id, t.id)}>Ack</button>}
                    {t.status !== 'done' && <button className="btn small" onClick={() => R.advanceTask(r.id, t.id)}>→</button>}
                  </div>
                </div>
              ))}
              {r.tasks.length === 0 && <div className="sub">No tasks yet.</div>}
            </div>
          </Section>

          <Section n={8} title="Communicate / Log Interaction">
            <div className="form-grid">
              <div className="field"><label>Channel</label><select value={msgCh} onChange={(e) => setMsgCh(e.target.value as Channel)}>{CHANNELS.map((c) => <option key={c} value={c}>{chLabel[c]}</option>)}</select></div>
              <div className="field"><label>Direction</label><select value={msgDir} onChange={(e) => setMsgDir(e.target.value as 'in' | 'out')}><option value="out">Outbound (we sent)</option><option value="in">Inbound (client said)</option></select></div>
              <div className="field full"><label>Message / note</label><textarea value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Reply, status update, question, or logged verbal update…" /></div>
            </div>
            <button className="btn small" style={{ marginTop: 8 }} onClick={() => { if (msg) { R.logInteraction(r.id, msgCh, msgDir, msg); setMsg(''); } }}>Log interaction</button>
          </Section>

          <Section n={9} title="Request Missing Information">
            <div className="chip-row">
              {MISSING.map((m) => (
                <button key={m} className={`select-chip${missSel.includes(m) ? ' on' : ''}`} onClick={() => setMissSel((s) => s.includes(m) ? s.filter((x) => x !== m) : [...s, m])}>{m}</button>
              ))}
            </div>
            {r.missingInfo.length > 0 && <div className="sub" style={{ marginTop: 6 }}>Outstanding: {r.missingInfo.join(', ')}</div>}
            <button className="btn small" style={{ marginTop: 8 }} onClick={() => { if (missSel.length) { R.requestInfo(r.id, missSel); setMissSel([]); } }}>Request from client</button>
          </Section>

          <Section n={10} title="Track Follow-Up">
            <div className="form-grid">
              <div className="field"><label>Date</label><input placeholder="e.g. Fri 2:00 PM" value={fuDate} onChange={(e) => setFuDate(e.target.value)} /></div>
              <div className="field"><label>Note</label><input value={fuNote} onChange={(e) => setFuNote(e.target.value)} /></div>
            </div>
            {r.followUp && <div className="sub" style={{ marginTop: 6 }}>Scheduled: {r.followUp.date} — {r.followUp.note}</div>}
            <button className="btn small" style={{ marginTop: 8 }} onClick={() => { if (fuDate) { R.scheduleFollowUp(r.id, fuDate, fuNote); setFuDate(''); setFuNote(''); } }}>Schedule follow-up</button>
          </Section>

          <Section n={11} title="Update Status">
            <select value={r.status} onChange={(e) => R.updateStatus(r.id, e.target.value as ReqStatus)}>
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </Section>

          <Section n={12} title="Attach Files">
            <button className="btn small" onClick={() => fileRef.current?.click()}>⬆ Upload file</button>
            <input ref={fileRef} type="file" multiple hidden onChange={(e) => { Array.from(e.target.files || []).forEach((f) => R.attachFile(r.id, f.name, 'upload')); }} />
            {r.files.length > 0 && <div className="file-chips" style={{ marginTop: 8 }}>{r.files.map((f) => <span className="file-chip" key={f.id}>📎 {f.name}</span>)}</div>}
          </Section>

          <Section n={13} title="Escalate Issue">
            <input placeholder="Reason — blocked / late / urgent…" value={esc} onChange={(e) => setEsc(e.target.value)} />
            <button className="btn small danger" style={{ marginTop: 8 }} onClick={() => { if (esc) { R.escalate(r.id, esc); setEsc(''); } }}>Escalate to Operations</button>
            {r.escalation && <div className="portal-banner warn" style={{ marginTop: 8 }}>Escalated: {r.escalation}</div>}
          </Section>

          <Section n={14} title="Close Request">
            <input placeholder="Resolution note / evidence…" value={closeNote} onChange={(e) => setCloseNote(e.target.value)} />
            <button className="btn small primary" style={{ marginTop: 8 }} disabled={r.status === 'closed'} onClick={() => { if (closeNote) R.closeRequest(r.id, closeNote); }}>{r.status === 'closed' ? 'Closed' : 'Close request'}</button>
          </Section>

          <Section n={15} title="Interaction History">
            <div className="record">
              {[...r.interactions].reverse().map((it) => (
                <div className="record-row" key={it.id}>
                  <span className="record-dot" style={{ background: it.dir === 'in' ? 'var(--neon-blue)' : it.dir === 'out' ? 'var(--gn)' : 'var(--tx3)' }} />
                  <div className="record-main"><strong>{it.dir === 'sys' ? 'System' : it.dir === 'in' ? `Client · ${chLabel[it.channel]}` : `Us · ${chLabel[it.channel]}`}</strong><p>{it.text}</p></div>
                  <span className="record-time">{it.time}</span>
                </div>
              ))}
            </div>
          </Section>
        </div>
      </aside>
    </>
  );
}
